// src/data/trips.ts — trips and the roster (build step 2).
//
// Every function here is one Supabase call. There is no client-side cache and
// no query library: screens load what they need on mount and reload after a
// write. For a group of six people over ten days that is a handful of small
// queries, and it means there is never a stale local copy to reconcile with
// what someone else just added from their phone.

import { supabase } from '../lib/supabase';
import { run, runExpectingRows, unwrap } from '../lib/errors';
import type { BalanceRow, TripMemberRow, TripPreview, TripRow } from './types';

/** A trip in the list, with the signed-in user's own position in it. */
export interface TripSummary {
  trip: TripRow;
  netBase: number;
  memberCount: number;
}

export async function listTrips(): Promise<TripSummary[]> {
  const trips = await unwrap<TripRow[]>(
    supabase
      .from('trips')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: false }),
  );

  if (trips.length === 0) return [];

  // One query for every trip's balances rather than one per trip. RLS already
  // limits this to trips the caller is on, so there is nothing to filter by.
  const balances = await unwrap<BalanceRow[]>(
    supabase
      .from('trip_member_balances')
      .select('*')
      .in('trip_id', trips.map((t) => t.id)),
  );

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id ?? null;

  return trips.map((trip) => {
    const forTrip = balances.filter((b) => b.trip_id === trip.id);
    const mine = forTrip.find((b) => b.user_id === userId);
    return {
      trip,
      netBase: Number(mine?.net_base ?? 0),
      memberCount: forTrip.length,
    };
  });
}

export async function getTrip(tripId: string): Promise<TripRow> {
  return unwrap<TripRow>(
    supabase.from('trips').select('*').eq('id', tripId).is('deleted_at', null).single(),
  );
}

export async function listMembers(tripId: string): Promise<TripMemberRow[]> {
  return unwrap<TripMemberRow[]>(
    supabase
      .from('trip_members')
      .select('*')
      .eq('trip_id', tripId)
      .neq('status', 'removed')
      .order('created_at', { ascending: true }),
  );
}

export async function listBalances(tripId: string): Promise<BalanceRow[]> {
  return unwrap<BalanceRow[]>(
    supabase.from('trip_member_balances').select('*').eq('trip_id', tripId),
  );
}

export interface NewTrip {
  name: string;
  baseCurrency: string;
  localCurrency?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
}

/**
 * Creates the trip and seats the caller as owner in one transaction. See
 * create_trip() — doing this as two inserts can strand a trip with an empty
 * roster, which RLS then makes invisible to the person who created it.
 */
export async function createTrip(input: NewTrip): Promise<TripRow> {
  return unwrap<TripRow>(
    supabase
      .rpc('create_trip', {
        p_name: input.name,
        p_base_currency: input.baseCurrency,
        p_local_currency: input.localCurrency ?? null,
        p_starts_on: input.startsOn ?? null,
        p_ends_on: input.endsOn ?? null,
      }),
  );
}

/** Owner only, enforced by the "owner updates trip" policy. */
export async function updateTrip(
  tripId: string,
  patch: Partial<Pick<TripRow, 'name' | 'base_currency' | 'local_currency' | 'status' | 'starts_on' | 'ends_on'>>,
): Promise<void> {
  await runExpectingRows(
    supabase.from('trips').update(patch).eq('id', tripId).select('id'),
    'Only the trip owner can change the trip’s settings.',
  );
}

/** Owner only. Soft delete, per the contract. */
export async function deleteTrip(tripId: string): Promise<void> {
  await runExpectingRows(
    supabase.from('trips').update({ deleted_at: new Date().toISOString() }).eq('id', tripId).select('id'),
    'Only the trip owner can delete this trip.',
  );
}

/**
 * Adds a seat with no account attached — non-negotiable #3. Anyone on the trip
 * can do this; the contract does not mark it owner-only, and on day one
 * everybody is adding everybody.
 */
export async function addMember(tripId: string, displayName: string): Promise<TripMemberRow> {
  return unwrap<TripMemberRow>(
    supabase
      .from('trip_members')
      .insert({ trip_id: tripId, display_name: displayName.trim() })
      .select('*')
      .single(),
  );
}

export async function renameMember(memberId: string, displayName: string): Promise<void> {
  await run(supabase.rpc('rename_member', { p_member_id: memberId, p_display_name: displayName }));
}

/** Owner only, and refused if they appear anywhere in the money. */
export async function removeMember(memberId: string): Promise<void> {
  await run(supabase.rpc('remove_member', { p_member_id: memberId }));
}

/** Reads the trip behind an invite code without joining it. */
export async function previewTrip(code: string): Promise<TripPreview> {
  return unwrap<TripPreview>(supabase.rpc('get_trip_preview', { p_code: code }));
}

/**
 * Joins a trip. Pass a seat id to claim that seat, or nothing to be added as a
 * new person. Returns the trip id.
 */
export async function joinTrip(code: string, memberId?: string | null): Promise<string> {
  return unwrap<string>(supabase.rpc('join_trip', { p_code: code, p_member_id: memberId ?? null }));
}
